import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  CalendarDaysIcon,
  ClockIcon,
  VideoCameraIcon,
  MapPinIcon,
  ArrowRightIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge } from '@/components/ui'
import { eventsApi } from '@/api'
import type { Event } from '@/api/events'
import { format, parseISO, differenceInDays, differenceInHours, differenceInMinutes, isPast, isFuture, isToday } from 'date-fns'
import { it } from 'date-fns/locale'

export default function PersonalAgendaWidget() {
  const [myEvents, setMyEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchMyEvents = async () => {
      try {
        const data = await eventsApi.getMyUpcomingEvents(5)
        setMyEvents(data)
      } catch (error) {
        console.error('Error fetching my events:', error)
        setMyEvents([])
      } finally {
        setLoading(false)
      }
    }

    fetchMyEvents()
  }, [])

  const getTimeUntil = (dateString: string): string => {
    const eventDate = parseISO(dateString)
    const now = new Date()

    if (isPast(eventDate)) return 'Concluso'

    const days = differenceInDays(eventDate, now)
    const hours = differenceInHours(eventDate, now) % 24
    const minutes = differenceInMinutes(eventDate, now) % 60

    if (days > 7) {
      return format(eventDate, 'd MMM', { locale: it })
    } else if (days > 0) {
      return `Tra ${days}g ${hours}h`
    } else if (hours > 0) {
      return `Tra ${hours}h ${minutes}m`
    } else if (minutes > 0) {
      return `Tra ${minutes}m`
    } else {
      return 'In corso'
    }
  }

  const isEventOngoing = (start: string, end: string): boolean => {
    return isPast(parseISO(start)) && isFuture(parseISO(end))
  }

  const isEventSoon = (dateString: string): boolean => {
    const eventDate = parseISO(dateString)
    const minutesUntil = differenceInMinutes(eventDate, new Date())
    return minutesUntil >= 0 && minutesUntil <= 30
  }

  if (loading) {
    return (
      <Card>
        <CardHeader title="La mia agenda" />
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </Card>
    )
  }

  return (
    <Card className="h-full">
      <CardHeader
        title="La mia agenda"
        action={
          <Link
            to="/events/my-events"
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            Tutti <ArrowRightIcon className="h-4 w-4 ml-1" />
          </Link>
        }
      />

      {myEvents.length > 0 ? (
        <div className="space-y-3">
          {myEvents.map((event) => {
            const ongoing = isEventOngoing(event.start_datetime, event.end_datetime)
            const soon = isEventSoon(event.start_datetime)
            const eventToday = isToday(parseISO(event.start_datetime))

            return (
              <Link
                key={event.id}
                to={`/events/${event.slug}`}
                className={`
                  block p-3 rounded-lg border transition-all
                  ${ongoing
                    ? 'bg-green-50 border-green-200 hover:bg-green-100'
                    : soon
                      ? 'bg-orange-50 border-orange-200 hover:bg-orange-100'
                      : eventToday
                        ? 'bg-primary-50 border-primary-200 hover:bg-primary-100'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }
                `}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {event.title}
                      </p>
                      {ongoing && (
                        <Badge variant="success" size="sm">
                          <PlayIcon className="h-3 w-3 mr-1" />
                          In corso
                        </Badge>
                      )}
                      {soon && !ongoing && (
                        <Badge variant="warning" size="sm">
                          A breve
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <CalendarDaysIcon className="h-3 w-3" />
                        {format(parseISO(event.start_datetime), 'd MMM', { locale: it })}
                      </span>
                      <span className="flex items-center gap-1">
                        <ClockIcon className="h-3 w-3" />
                        {format(parseISO(event.start_datetime), 'HH:mm')}
                      </span>
                      {event.is_online ? (
                        <span className="flex items-center gap-1 text-blue-600">
                          <VideoCameraIcon className="h-3 w-3" />
                          Online
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <MapPinIcon className="h-3 w-3" />
                          In sede
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-right">
                    <span className={`
                      text-xs font-medium px-2 py-1 rounded-full
                      ${ongoing
                        ? 'bg-green-200 text-green-800'
                        : soon
                          ? 'bg-orange-200 text-orange-800'
                          : 'bg-gray-200 text-gray-700'
                      }
                    `}>
                      {getTimeUntil(event.start_datetime)}
                    </span>

                    {/* Quick Join Button for online events */}
                    {event.is_online && (ongoing || soon) && (
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          window.open(`/events/${event.slug}`, '_blank')
                        }}
                        className="mt-2 flex items-center justify-center w-full px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                      >
                        <PlayIcon className="h-3 w-3 mr-1" />
                        Partecipa
                      </button>
                    )}
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <CalendarDaysIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500 mb-3">
            Nessun evento in programma
          </p>
          <Link
            to="/events"
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
          >
            Scopri gli eventi <ArrowRightIcon className="h-4 w-4 ml-1" />
          </Link>
        </div>
      )}
    </Card>
  )
}
